/**
 * What a desk can make a counter do (docs/plans/handheld-fleet.html 11).
 *
 * The pattern is DESIRED STATE, not remote procedure call. The platform states
 * what it wants; the device notices on its next beat and converges; the
 * platform learns it converged from the ack. Nothing waits on a socket, nothing
 * needs a push service, and a device that is asleep gets the message when it
 * wakes - which is when somebody is holding it.
 *
 * ## The enum is CLOSED, and that is the security property
 *
 * There is deliberately no kind that carries a path, a URL, a package name or a
 * script (plan 18): every member below is a thing the app already does, wired
 * to a handler it already has. An open command - "run this", "fetch that" - is
 * a remote shell with a device token as its password, on hardware sitting
 * behind a bakery counter with a printer and a till. Adding a kind is a new
 * enum member here, a new handler on the device, and a new pair of catalogue
 * titles; it is never a new ARGUMENT on an existing one.
 *
 * `SHOW_MESSAGE` is the one member that carries anything, and what it carries
 * is one sentence for a human to read - never anything the app interprets.
 *
 * ## Why the device may answer UNSUPPORTED
 *
 * A newer platform talks to an older app. The device's switch over these kinds
 * is exhaustive, and its default branch acknowledges `UNSUPPORTED` rather than
 * crashing or staying silent - so a kind added this week is a clean refusal on
 * a counter that has not taken the build yet, and the operator sees WHY the
 * ring never rang instead of watching a command sit undelivered for ever.
 *
 * ## Where these shapes are read
 *
 * The device reads `DeviceCommand` off the heartbeat answer and writes
 * `DeviceCommandAck` back; the operator reads `DeviceCommandRecord` on the
 * device page. Three mounts, one file, for the same reason
 * `handheld-telemetry.ts` beside it is one file: a shape restated per mount is
 * a shape that drifts.
 */

import { z } from 'zod';
import { Instant, Uuid } from './common';

export const DeviceCommandKind = z.enum([
  /** 20 s of a tone that is NOT the arrival alarm, to find a device or get attention. */
  'RING',
  /** One slip: shop name, device label, build, time - proves the paper path end to end. */
  'TEST_PRINT',
  /** The log ring and the last crash, uploaded to the fleet bucket (plan 13). */
  'UPLOAD_DIAGNOSTICS',
  /** Finish activities and exit; Android relaunches on the next tap, the token survives. */
  'RESTART_APP',
  /** Drop every cached snapshot and re-read the queue and the shop. */
  'RESYNC',
  /** One French sentence from the operator, dismissible; for "call us back". */
  'SHOW_MESSAGE',
]);
export type DeviceCommandKind = z.infer<typeof DeviceCommandKind>;

/** One sentence, and the cap the screen counts against - stated once, here. */
export const DEVICE_MESSAGE_MAX_CHARS = 200;

/**
 * The only arguments any command carries, and they are one sentence for a
 * person to read. Nothing here is interpreted by the app.
 *
 * The boundary TRIMS, and then demands something is left. Whitespace is not a
 * message: a text that is one newline arrives at a counter as an empty dialog
 * with a dismiss button and no explanation, and a caller that pads its sentence
 * should not lose the tail of it to the cap either. Trimming here rather than
 * at each caller is what makes "no empty message reaches a counter" a property
 * of the wire instead of a rule three clients have to remember - and the cap is
 * measured on what actually gets shown.
 */
export const DeviceCommandArgs = z.object({
  text: z.string().trim().min(1).max(DEVICE_MESSAGE_MAX_CHARS),
});
export type DeviceCommandArgs = z.infer<typeof DeviceCommandArgs>;

/**
 * One command, as the device receives it on its beat.
 *
 * The refinement is what makes "only SHOW_MESSAGE has arguments" a rule the
 * wire enforces in both directions rather than a comment: a RING carrying text
 * is refused, and a SHOW_MESSAGE with nothing to show is refused too - the
 * second being the one that would otherwise reach a counter as an empty dialog
 * nobody can explain.
 */
export const DeviceCommand = z
  .object({
    id: Uuid,
    kind: DeviceCommandKind,
    /** Only SHOW_MESSAGE has one, and the schema says so per kind. */
    args: DeviceCommandArgs.optional(),
    issuedAt: Instant,
    /**
     * After this the device ignores it and the platform marks it EXPIRED - a
     * RING from last Tuesday must not fire.
     */
    expiresAt: Instant,
  })
  .refine((command) => (command.kind === 'SHOW_MESSAGE') === (command.args !== undefined), {
    message: 'SHOW_MESSAGE carries a text, and no other kind carries arguments',
    path: ['args'],
  });
export type DeviceCommand = z.infer<typeof DeviceCommand>;

/**
 * How a command ended, as the DEVICE may report it.
 *
 * `UNSUPPORTED` is the whole compatibility story: a build that predates a kind
 * answers it instead of failing, so an operator reads "this counter's app does
 * not know that yet" rather than "nothing happened".
 */
export const DeviceCommandAckOutcome = z.enum(['DONE', 'FAILED', 'UNSUPPORTED']);
export type DeviceCommandAckOutcome = z.infer<typeof DeviceCommandAckOutcome>;

/**
 * ...and how it may end on the PLATFORM's side, which is one member more.
 *
 * `EXPIRED` is never sent by a device: it is what the platform writes about a
 * command no device ever came back for. It is on this enum rather than on a
 * separate flag because "how did this end" has one answer per command, and two
 * fields for it are two fields that can disagree.
 *
 * Spelled out rather than spread off `DeviceCommandAckOutcome.options`: zod's
 * `.options` is an array of the union, not a tuple of literals, so a spread
 * would widen every member to `string` and lose the exhaustive switch that
 * makes a new outcome a typecheck failure on the screens that render one.
 */
export const DeviceCommandOutcome = z.enum(['DONE', 'FAILED', 'UNSUPPORTED', 'EXPIRED']);
export type DeviceCommandOutcome = z.infer<typeof DeviceCommandOutcome>;

/** What a device sends back, once, per command id; replaying it changes nothing. */
export const DeviceCommandAck = z.object({
  outcome: DeviceCommandAckOutcome,
  /** Free text from the device for an operator to read - a printer error, say. */
  detail: z.string().max(200).optional(),
});
export type DeviceCommandAck = z.infer<typeof DeviceCommandAck>;

/**
 * One command as an OPERATOR reads it on the device page: the command, who
 * issued it, and everything that has happened to it since.
 *
 * `deliveredAt` is stamped on the first beat that carried it and never moved,
 * so "issued 07:02, delivered 07:02, acknowledged 07:03" is the whole life of a
 * ring; a command still reading `deliveredAt: null` at 07:10 is a counter that
 * is not beating, which is a different problem and a different fix.
 */
export const DeviceCommandRecord = z.object({
  id: Uuid,
  kind: DeviceCommandKind,
  args: DeviceCommandArgs.nullable(),
  issuedAt: Instant,
  /** The operator's verified address, as every audit row records them. */
  issuedBy: z.string().max(200),
  expiresAt: Instant,
  deliveredAt: Instant.nullable(),
  acknowledgedAt: Instant.nullable(),
  outcome: DeviceCommandOutcome.nullable(),
  detail: z.string().max(200).nullable(),
});
export type DeviceCommandRecord = z.infer<typeof DeviceCommandRecord>;

/**
 * How many commands one beat may carry.
 *
 * A cap rather than a page: five is more than an operator ever issues at once,
 * and a counter handed a hundred queued rings would spend its morning
 * converging on a Tuesday afternoon. The rest stay pending and ride the beats
 * that follow, oldest first, until they are acknowledged or expire.
 */
export const DEVICE_COMMANDS_PER_BEAT = 5;

/**
 * How long a command stands before the platform gives up on it.
 *
 * Thirty minutes for everything the operator is watching happen - a ring, a
 * test print, a restart - because a command that fires an hour after it was
 * issued is a command nobody is standing beside. Twenty-four hours for a
 * message, which is addressed to whoever next picks the device up.
 */
export const DEVICE_COMMAND_EXPIRY_MS = 30 * 60 * 1000;
export const DEVICE_MESSAGE_EXPIRY_MS = 24 * 60 * 60 * 1000;

/** The window one kind is given, stated once for the issuer and the tests. */
export function deviceCommandExpiryMs(kind: DeviceCommandKind): number {
  return kind === 'SHOW_MESSAGE' ? DEVICE_MESSAGE_EXPIRY_MS : DEVICE_COMMAND_EXPIRY_MS;
}
